import { memo, useCallback, useMemo, useState } from 'react'
import { IndicatorInput, IndicatorInput_InputType } from '../../../../proto/generated/indicator'
import {
    Box,
    Checkbox,
    Divider,
    FormControlLabel,
    IconButton,
    MenuItem,
    Select,
    SvgIcon,
    TextField,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material'
import type { SvgIconProps } from '@mui/material/SvgIcon'
import type { Theme } from '@mui/material/styles'
import type { SystemStyleObject } from '@mui/system'
import InfoIcon from '@mui/icons-material/Info'
import {
    getColorSelectorActiveOutlineSx,
    getColorSelectorMatchedFieldOutlineSx,
} from '../../../colorSelector/colorSelectorChrome'
import { getIndicatorInputSelectOptions, getIndicatorPriceSourceLabel, IndicatorOutputSourceOption, isIndicatorOutputSource, isIndicatorPriceSourceInput, isIndicatorSelectInput } from '../../../../util/indicators'
import ColorSelector from '../../../colorSelector/colorSelector'
import { isPatternLabelColorInput } from '../../../../util/patternIndicators'

const SelectDropdownIcon = ({ sx, ...props }: SvgIconProps) => (
    <SvgIcon
        {...props}
        viewBox="0 0 24 24"
        sx={{ fontSize: 18, width: 18, height: 18, ...sx }}
    >
        <path d="M6.5 9L12 14.5L17.5 9" stroke="currentColor" fill="none" />
    </SvgIcon>
)

const LABEL_HORIZONTAL_PADDING = 20
const MIN_LABEL_COLUMN_WIDTH = 90

const measureTextWidth = (text: string, font: string): number => {
    if (!text || typeof document === 'undefined') return 0
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    if (!ctx) return 0
    ctx.font = font
    return ctx.measureText(text).width
}

const getBody2CanvasFont = (theme: Theme): string => {
    const { fontWeight, fontSize, fontFamily } = theme.typography.body2
    const size = typeof fontSize === 'number' ? `${fontSize}px` : fontSize
    return `${fontWeight} ${size} ${fontFamily}`
}

const getLabelColumnWidth = (labels: string[], font: string): number => {
    const maxTextWidth = labels.reduce((max, label) => Math.max(max, measureTextWidth(label, font)), 0)
    return Math.max(MIN_LABEL_COLUMN_WIDTH, Math.ceil(maxTextWidth) + LABEL_HORIZONTAL_PADDING)
}

const inputRowSx = {
    py: '8px',
    px: 0,
    minHeight: 50,
    boxSizing: 'border-box' as const,
    alignItems: 'center',
}

const sectionHeaderRowSx = {
    pt: '8px',
    pb: 0,
    px: 0,
    boxSizing: 'border-box' as const,
    alignItems: 'center',
}

const inputLabelProps = {
    variant: 'body2' as const,
    color: 'text.secondary' as const,
    fontWeight: 400,
}

const getInputLabelColumnSx = (widthPx: number) => ({
    p: 0,
    width: `${widthPx}px`,
    minWidth: `${widthPx}px`,
    maxWidth: `${widthPx}px`,
    flex: `0 0 ${widthPx}px`,
    display: 'flex',
    alignItems: 'center',
    overflow: 'hidden',
})

/** 18px icon + 2px inset from the right edge of the 100px control */
const SELECT_ICON_RESERVE_PX = 20

export type SeriesGroupInputProps = {
    inputValues: Record<string, any>;
    fields: IndicatorInput[];
    onInputDraftChange: (title: string, value: any) => void;
    onInputChange: (title: string, value: any) => void;
    indicatorOutputSources?: IndicatorOutputSourceOption[];
    getSourceLabel?: (value: string) => string;
    getInputTitle?: (input: IndicatorInput) => string;
    hideGroupHeaders?: boolean;
};

const parseNumericInputValue = (raw: string): number | '' =>
    raw === '' ? '' : Number(raw)

const formatNumericDisplayValue = (value: any, defval?: string): string | number => {
    if (value === '' || (typeof value === 'number' && isNaN(value))) return ''
    if (value !== undefined && value !== null) return value
    return defval ?? ''
}

const SeriesGroupInputs = ({
    inputValues,
    fields,
    onInputDraftChange,
    onInputChange,
    indicatorOutputSources = [],
    getSourceLabel,
    getInputTitle,
    hideGroupHeaders = false,
}: SeriesGroupInputProps) => {
    const theme = useTheme()
    const [openSelectCode, setOpenSelectCode] = useState<string | null>(null)
    const groupedFields = fields.reduce<Record<string, IndicatorInput[]>>((acc, field) => {
        const group = field.group || ''
        if (!acc[group]) acc[group] = []
        acc[group].push(field)
        return acc
    }, {})

    const resolveInputTitle = useCallback(
        (input: IndicatorInput) => getInputTitle?.(input) ?? input.title,
        [getInputTitle],
    )

    const labelColumnWidth = useMemo(() => {
        const font = getBody2CanvasFont(theme)
        const labels = fields.map((field) => resolveInputTitle(field)).filter(Boolean)
        return getLabelColumnWidth(labels, font)
    }, [fields, theme, resolveInputTitle])

    const inputLabelColumnSx = useMemo(
        () => getInputLabelColumnSx(labelColumnWidth),
        [labelColumnWidth]
    )

    const inputControlSx = useMemo(
        (): SystemStyleObject<Theme> => ({
            width: '100px',
            flexShrink: 0,
            ...getColorSelectorMatchedFieldOutlineSx(theme),
            '& .MuiInputBase-root': {
                height: '34px',
                minHeight: '34px',
            },
            '& .MuiInputBase-input': {
                padding: '6px 10px',
                boxSizing: 'border-box' as const,
            },
        }),
        [theme]
    )

    const selectControlSx = useMemo(
        (): SystemStyleObject<Theme> => ({
            ...inputControlSx,
            '& .MuiSelect-select': {
                padding: `6px ${SELECT_ICON_RESERVE_PX}px 6px 10px !important`,
                minHeight: '22px !important',
                boxSizing: 'border-box' as const,
                display: 'block',
                width: '100%',
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
            },
            '& .MuiOutlinedInput-input': {
                paddingRight: `${SELECT_ICON_RESERVE_PX}px !important`,
            },
            '& .MuiSelect-icon': {
                fontSize: '18px',
                width: '18px',
                height: '18px',
                top: 'calc(50% - 9px)',
                right: '2px',
                '& svg': {
                    width: '18px',
                    height: '18px',
                },
            },
        }),
        [theme, inputControlSx]
    )

    const selectMenuProps = useMemo(
        () => ({
            sx: { zIndex: 13000 },
        }),
        []
    )

    const resolveSourceLabel = useCallback((value: string, input: IndicatorInput) => {
        if (getSourceLabel) {
            return getSourceLabel(value)
        }
        if (isIndicatorPriceSourceInput(input)) {
            if (isIndicatorOutputSource(value)) {
                return indicatorOutputSources.find((option) => option.value === value)?.label ?? value
            }
            return getIndicatorPriceSourceLabel(value)
        }
        return value
    }, [getSourceLabel, indicatorOutputSources])

    const renderSelectOptions = useCallback((input: IndicatorInput) => {
        return getIndicatorInputSelectOptions(input).map((option) => (
            <MenuItem value={option} key={option}>
                {resolveSourceLabel(option, input)}
            </MenuItem>
        ))
    }, [resolveSourceLabel])

    const renderIndicatorSourceOptions = useCallback(() => {
        if (!indicatorOutputSources.length) return null

        return [
            <MenuItem
                key="indicator-source-divider"
                disabled
                sx={{
                    opacity: 1,
                    py: 0.5,
                    px: 1,
                    minHeight: 'auto',
                    pointerEvents: 'none',
                }}
            >
                <Divider
                    sx={{
                        width: '100%',
                        my: 0.5,
                        backgroundColor: theme.palette.mode === 'dark' ? '#434651' : '#e0e3eb',
                    }}
                />
            </MenuItem>,
            ...indicatorOutputSources.map((option) => (
                <MenuItem value={option.value} key={option.value}>
                    {option.label}
                </MenuItem>
            )),
        ]
    }, [indicatorOutputSources, theme.palette.mode])

    const handleSelectChange = useCallback((code: string, value: string) => {
        onInputChange(code, value)
        setOpenSelectCode(null)
    }, [onInputChange])

    return (
        <Box>
            {Object.entries(groupedFields).map(([groupName, groupFields]) => (
                <Box key={groupName || 'default'}>
                    {groupName && !hideGroupHeaders ? (
                        <Box sx={sectionHeaderRowSx} display="flex" alignItems="center">
                            <Typography fontSize="12px" color="#6a6d78">
                                {groupName}
                            </Typography>
                        </Box>
                    ) : null}
                    {groupFields.map((i) => {
                        const isSelectMenuOpen = openSelectCode === i.code
                        const inputTitle = resolveInputTitle(i)
                        return (
                        <Box
                            key={i.code}
                            sx={{ ...inputRowSx, display: 'flex', alignItems: 'center' }}
                        >
                            <Box sx={inputLabelColumnSx}>
                                <Typography {...inputLabelProps} noWrap title={inputTitle}>
                                    {inputTitle}
                                </Typography>
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0, p: 0, display: 'flex', alignItems: 'center', gap: '4px' }}>
                                {[IndicatorInput_InputType.INTEGER, IndicatorInput_InputType.FLOAT].includes(i.type) && (
                                    <TextField
                                        value={formatNumericDisplayValue(inputValues[i.code], i.defval)}
                                        type="number"
                                        inputProps={{
                                            min: i.minval,
                                            max: i.maxval,
                                            step: 1,
                                        }}
                                        size="small"
                                        sx={inputControlSx}
                                        onChange={({ target }) =>
                                            onInputDraftChange(i.code, parseNumericInputValue(target.value))
                                        }
                                        onBlur={({ target }) =>
                                            onInputChange(i.code, parseNumericInputValue(target.value))
                                        }
                                    />
                                )}

                                {isIndicatorSelectInput(i) && (
                                    <Select
                                        value={inputValues[i.code] ?? i.defval ?? ''}
                                        size="small"
                                        open={isSelectMenuOpen}
                                        onOpen={() => setOpenSelectCode(i.code)}
                                        onClose={() => setOpenSelectCode(null)}
                                        MenuProps={selectMenuProps}
                                        sx={{
                                            ...selectControlSx,
                                            ...(isSelectMenuOpen
                                                ? getColorSelectorActiveOutlineSx(theme)
                                                : undefined),
                                        }}
                                        IconComponent={SelectDropdownIcon}
                                        renderValue={(value) => {
                                            const displayValue = resolveSourceLabel(String(value ?? ''), i)
                                            return (
                                            <Typography
                                                component="span"
                                                variant="body2"
                                                noWrap
                                                title={displayValue}
                                                sx={{
                                                    display: 'block',
                                                    width: '100%',
                                                    maxWidth: '100%',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    lineHeight: '22px',
                                                }}
                                            >
                                                {displayValue}
                                            </Typography>
                                            )
                                        }}
                                        onChange={({ target }) => handleSelectChange(i.code, target.value)}
                                    >
                                        {isIndicatorPriceSourceInput(i)
                                            ? [
                                                ...renderSelectOptions(i),
                                                ...(renderIndicatorSourceOptions() ?? []),
                                            ]
                                            : renderSelectOptions(i)}
                                    </Select>
                                )}

                                {isPatternLabelColorInput(i) && (
                                    <ColorSelector
                                        compact
                                        showOnlySwatch
                                        color={String(inputValues[i.code] ?? i.defval ?? '#2962FF')}
                                        opacity={1}
                                        onColorChange={(clr) => onInputChange(i.code, clr)}
                                    />
                                )}

                                {i.type === IndicatorInput_InputType.BOOLEAN && (
                                    <FormControlLabel
                                        sx={{ m: 0 }}
                                        control={
                                            <Checkbox
                                                checked={
                                                    inputValues[i.code] === 'false'
                                                        ? false
                                                        : !!inputValues[i.code] || false
                                                }
                                                size="small"
                                                sx={{
                                                    p: 0,
                                                    '&.Mui-checked': {
                                                        color: (theme) => theme.palette.primary.main,
                                                    },
                                                }}
                                                onChange={() =>
                                                    onInputChange(
                                                        i.code,
                                                        !(
                                                            inputValues[i.code] === 'false'
                                                                ? false
                                                                : !!inputValues[i.code] || false
                                                        )
                                                    )
                                                }
                                            />
                                        }
                                        label=""
                                    />
                                )}

                                {i.tooltip && i.tooltip.trim() && (
                                    <Tooltip title={i.tooltip} arrow placement="top">
                                        <IconButton size="small" sx={{ p: 0.5 }}>
                                            <InfoIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>
                        </Box>
                        )
                    })}
                </Box>
            ))}
        </Box>
    )
}

export default memo(SeriesGroupInputs)
